<template>
    <div class="mt-10">
        <div class="grid md:grid-cols-3 col-gap-2">
            <div class="bg-white p-4 mb-2 flex flex-col border">
                <div class="flex-1">
                    <div class="mb-3">
                        <label class="block text-gray-700 text-sm font-semibold mb-2">{{ $t("currency") }}</label>
                        <a-select v-model="query.currencyCode" class="w-full" size="large">
                            <a-select-option v-for="option in currencies" :value="option.value">
                                {{ option.value }}
                            </a-select-option>
                        </a-select>
                    </div>
                    <div class="mb-3">
                        <label class="block text-gray-700 text-sm font-semibold mb-2">{{ $t("startDate") }}</label>
                        <a-date-picker v-model="query.postingDateFrom" size="large" class="w-full"/>
                    </div>
                    <div class="mb-3">
                        <label class="block text-gray-700 text-sm font-semibold mb-2">{{ $t("endDate") }}</label>
                        <a-date-picker v-model="query.postingDateTo" size="large" class="w-full"/>
                    </div>
                    <div class="mb-3">
                        <label class="block text-gray-700 text-sm font-semibold mb-2">{{ $t("transactionType") }}</label>
                        <a-select v-model="query.txType" class="w-full" size="large">
                            <a-select-option v-for="option in txTypes" :value="option.value">
                                {{ option.label }}
                            </a-select-option>
                        </a-select>
                    </div>
                </div>
                <div class="mt-2 w-auto flex block">
                    <div class="flex-grow">
                        <a-button type="primary" @click="search" class="inline-block">
                            {{ $t("search") }}
                        </a-button>
                        <a-button @click="resetFilters"
                                  v-if="query.txType || query.postingDateFrom || query.postingDateTo"
                                  class="ml-2 inline-block">
                            {{ $t("reset") }}
                        </a-button>
                    </div>
                    <div>
                        <a-button type="link" @click="download" class="inline-block px-0 text-sm">
                            {{ $t("exportExcel") }}
                        </a-button>
                    </div>
                </div>
            </div>
            <div class="md:col-span-2 bg-white p-4 mb-2 border">
                <div class="flex flex-wrap mb-2">
                    <div v-for="(period, index) in periods"
                         class="pb-1 mr-4 mb-2 cursor-pointer text-sm text-primary-500"
                         :class="[period.selected ? 'font-bold border-b border-primary-500 border-b-2' : '', periods.length - 1 === index ? 'mr-0' : '']"
                         :key="index"
                         @click="filterByPeriod(period.label)">
                        {{ period.label }}
                    </div>
                </div>
                <table class="table-auto bg-white w-full text-sm">
                    <tbody>
                    <tr class="border-b font-semibold hover:bg-gray-100">
                        <td class="py-2 pr-2">{{ $t("openingBalance") }}</td>
                        <td class="py-2 text-right">{{ accountStatement.openingBalance | currencyFormat(accountStatement.currencySymbol) }}</td>
                    </tr>
                    <tr v-for="item in accountStatement.turnovers" class="border-b hover:bg-gray-100" v-if="item.total">
                        <td class="py-2 pr-2">{{ item.txTitle }}</td>
                        <td class="py-2 text-right">{{ item.total | currencyFormat(accountStatement.currencySymbol) }}</td>
                    </tr>
                    <tr class="font-semibold hover:bg-gray-100">
                        <td class="py-2 pr-2">{{ $t("closingBalance") }}</td>
                        <td class="py-2 text-right">{{ accountStatement.closingBalance | currencyFormat(accountStatement.currencySymbol) }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <k-table :data-set="accountStatement.result" @change="tableChange" row-key="id" :query="query">
            <a-table-column title="Tx ID" key="id" data-index="id" :ellipsis="true"/>
            <a-table-column title="Date" key="postingTs" data-index="postingTs" :ellipsis="true">
                <template slot-scope="text">
                    {{ text | dateTime }}
                </template>
            </a-table-column>
            <a-table-column title="Transaction" key="txTitle" data-index="txTitle" :ellipsis="true"/>
            <a-table-column title="Borrower" key="companyName" data-index="companyName"
                            :ellipsis="true"/>
            <a-table-column title="Tranche number" key="trancheNumber" data-index="trancheNumber"
                            :ellipsis="true"
            >
                <template slot-scope="text, item">
                    <a class="text-primary-500" @click="openTranche(item.trancheUuid)">
                        {{ item.trancheNumber }}
                    </a>
                </template>
            </a-table-column>
            <a-table-column title="Amount" key="amount" data-index="amount" :ellipsis="true" align="right">
                <template slot-scope="text">{{ text | currencyFormat(accountStatement.currencySymbol) }}</template>
            </a-table-column>
            <a-table-column title="Amount reserved" key="amountReserved" data-index="amountReserved"
                            :ellipsis="true" align="right">
                <template slot-scope="text">{{ text | currencyFormat(accountStatement.currencySymbol) }}</template>
            </a-table-column>
            <a-table-column title="Balance" key="balance" data-index="balance" :ellipsis="true" align="right">
                <template slot-scope="text">
                    {{ text | currencyFormat(accountStatement.currencySymbol) }}
                </template>
            </a-table-column>
        </k-table>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    props: {
        accountStatement: {
            type: Object,
            required: true
        },
    },
    data() {
        return {
            query: {
                currencyCode: 'USD',
                txType: null,
                postingDateFrom: null,
                postingDateTo: null,
                pageSize: 100,
                page: 1,
            },
            currencies: [
                {value: 'USD'},
                {value: 'EUR'},
                {value: 'SGD'}
            ],
            txTypes: [
                {label: 'Deposit', value: 'DEPOSIT'},
                {label: 'Withdrawal request', value: 'WITHDRAWAL_REQUEST'},
                {label: 'Withdrawal', value: 'WITHDRAWAL'},
                {label: 'Subscription', value: 'SUBSCRIPTION'},
                {label: 'Settlement', value: 'SETTLEMENT'},
                {label: 'Repayment', value: 'REPAYMENT'}
            ]
        }
    },
    computed: {
        periods: () => {
            return [
                {
                    label: 'Today',
                    from: moment().format('YYYY-MM-DD'),
                    to: moment().format('YYYY-MM-DD'),
                },
                {
                    label: 'Yesterday',
                    from: moment().subtract(1, 'day').format('YYYY-MM-DD'),
                    to: moment().subtract(1, 'day').format('YYYY-MM-DD'),
                },
                {
                    label: 'Current week',
                    from: moment().startOf('week').format('YYYY-MM-DD'),
                    to: moment().endOf('week').format('YYYY-MM-DD'),
                },
                {
                    label: 'Last week',
                    from: moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD'),
                    to: moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD'),
                },
                {
                    label: 'Current month',
                    from: moment().startOf('month').format('YYYY-MM-DD'),
                    to: moment().endOf('month').format('YYYY-MM-DD'),
                },
                {
                    label: 'Last month',
                    from: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
                    to: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
                },
                {
                    label: 'All time',
                    from: null,
                    to: null,
                }
            ]
        }
    },
    async mounted() {
        await this.filterByPeriod('All time');
    },
    methods: {
        async filterByPeriod(label) {
            this.periods.forEach(p => p.selected = false);
            let index = this.periods.findIndex(i => i.label === label);
            this.periods[index].selected = true;
            this.query.postingDateFrom = this.periods[index].from;
            this.query.postingDateTo = this.periods[index].to;
            await this.refresh();
        },
        async search() {
            this.periods.forEach(p => p.selected = false);
            await this.refresh();
        },
        async refresh() {
            this.getStatements();
        },
        async getStatements() {
            this.$emit('get-account-statement', this.query);
        },
        async tableChange(query) {
            this.query = query;
            await this.refresh();
        },
        async resetFilters() {
            this.query.txType = null;
            await this.filterByPeriod("All time");
        },
        async download() {
            this.$emit('download-statement', this.query);
        },
        async openTranche(trancheUuid) {
            this.$emit('open-tranche', trancheUuid);
        },
    },
    filters: {
        dateTime: (value) => moment(value, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DD HH:mm')
    }
}
</script>


